import React from 'react';
import computingImage from '../../static/computing_image/computing_main.png';
import AnnotatedImage, { AutoImageProps } from '../automotivePage/CarImageComponent';
import { ApplicationsComputingAndEnterpriseSystemsComputingImageMapLinkTextsContent } from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';

// Strong normalization: NFKC + remove extra spaces + lowercase
const normalize = (s: string) =>
    String(s)
        .normalize('NFKC')
        .replace(/\u00A0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLocaleLowerCase();

// Further canonical version: remove common separators (hyphen, underscore, dash, spaces)
const canonical = (s: string) => normalize(s).replace(/[\s_\-–—]+/g, ''); // e.g. treat "usv-batterien" and "usvbatterien" as identical

function generateLabelToComponentMap(
    t: ApplicationsComputingAndEnterpriseSystemsComputingImageMapLinkTextsContent
): Record<string, string> {
    // Base mapping: normalized label → internal key
    const base: Record<string, string> = {
        [normalize(t.climateControl.content)]: 'climatecontrol',
        [normalize(t.upsBatteries.content)]: 'upsbatteries',
        [normalize(t.mainPowerDistribution.content)]: 'mainpowerdistribution',
        [normalize(t.edgeComputing.content)]: 'edgecomputing',
        [normalize(t.generator.content)]: 'generator',
        [normalize(t.networkPowerChiller.content)]: 'networkpowerchiller',
        [normalize(t.apc.content)]: 'apc',
        [normalize(t.pcsAndNotebooks.content)]: 'pcsandnotebooks',
        [normalize(t.server.content)]: 'server',
    };

    // Create an alias table: map canonical (separator-free) labels to the same key
    // Also include the internal key itself to handle cases like 'edgecomputing'
    const alias: Record<string, string> = {};
    Object.entries(base).forEach(([labelNorm, key]) => {
        alias[canonical(labelNorm)] = key; // label without separators → key
        alias[canonical(key)] = key; // internal key (and separator-free version) → key
    });

    // Keep both normalized and canonical forms for matching
    return { ...base, ...alias };
}

const DefaultComputing: React.FC<AutoImageProps> = ({ onAnnotationClick, contentTree }) => {
    const imageMap = contentTree.ComputingAndEnterpriseSystems.Computing.imageMapLinkTexts;
    const labelToComponentMap = generateLabelToComponentMap(imageMap);

    const annotations = [
        {
            label: imageMap.climateControl.content,
            pointX: 190,
            pointY: 460,
            labelX: 208,
            labelY: 300,
            lineStartAdjust: 9,
            lineToLabelGap: -10,
            lineToPointGap: -30,
        },
        {
            label: imageMap.upsBatteries.content,
            pointX: 350,
            pointY: 365,
            labelX: 370,
            labelY: 220,
            lineStartAdjust: 9,
            lineToLabelGap: -10,
            lineToPointGap: -30,
        },
        {
            label: imageMap.mainPowerDistribution.content,
            pointX: 580,
            pointY: 250,
            labelX: 598,
            labelY: 150,
            lineStartAdjust: 9,
            lineToLabelGap: -15,
            lineToPointGap: -30,
        },
        {
            label: imageMap.edgeComputing.content,
            pointX: 810,
            pointY: 105,
            labelX: 830,
            labelY: 35,
            lineStartAdjust: 9,
            lineToLabelGap: -15,
            lineToPointGap: -30,
        },
        {
            label: imageMap.networkPowerChiller.content,
            pointX: 920,
            pointY: 495,
            labelX: 938,
            labelY: 610,
            lineStartAdjust: 7,
            lineToLabelGap: -8,
            lineToPointGap: -12,
        },
        {
            label: imageMap.generator.content,
            pointX: 1055,
            pointY: 420,
            labelX: 1073,
            labelY: 530,
            lineStartAdjust: 7,
            lineToLabelGap: -10,
            lineToPointGap: -16,
        },
        {
            label: imageMap.apc.content,
            pointX: 760,
            pointY: 655,
            labelX: 780,
            labelY: 760,
            lineStartAdjust: 6,
            lineToLabelGap: -5,
            lineToPointGap: -10,
        },
        {
            label: imageMap.pcsAndNotebooks.content,
            pointX: 415,
            pointY: 695,
            labelX: 430,
            labelY: 965,
            lineStartAdjust: 7,
            lineToLabelGap: -5,
            lineToPointGap: -10,
        },
        {
            label: imageMap.server.content,
            pointX: 310,
            pointY: 750,
            labelX: 327,
            labelY: 900,
            lineStartAdjust: 7,
            lineToLabelGap: -5,
            lineToPointGap: -10,
        },
    ];

    return (
        <AnnotatedImage
            imageSrc={computingImage}
            annotations={annotations}
            onAnnotationClick={(rawLabel) => {
                const s = Array.isArray(rawLabel) ? rawLabel.join(' ') : String(rawLabel);

                // Try exact normalized match first, then canonical (separator-free) match
                const key = labelToComponentMap[normalize(s)] ?? labelToComponentMap[canonical(s)];

                if (!key) {
                    console.warn('[DefaultComputing] Unknown label; could not map to key', {
                        rawLabel,
                        normalized: normalize(s),
                        canonical: canonical(s),
                        // Show available keys (canonical form is clearer for debugging)
                        available: Object.keys(labelToComponentMap).slice(0, 20),
                    });
                    return; // Prevent passing undefined upward
                }

                onAnnotationClick(key);
            }}
            imageStyle={{
                marginBottom: '2rem',
                marginTop: '2rem',
                marginLeft: '1rem',
                width: '99%',
                height: 'auto',
            }}
            viewBox="0 0 1200 976"
            isDefaultImage
        />
    );
};

export { DefaultComputing };
